import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Card,
  Typography,
  Space,
  Tag,
  Collapse,
  Empty,
  Spin,
  message,
  Table,
  Button,
} from "antd";
import {
  GET_GEO_ALL, // ⭐ เพิ่ม
  HEADERS_EXPORT_BEN,
  POST_LOAN_DB2_V2,
  POST_LAWSUIT_DETAIL,
} from "../../../API/apiUrls";
import axios from "axios";
import { HomeOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AddAddressDetail from "./AddAddressDetail";

const { Text } = Typography;

const ImportAddressTr = ({ open, close, dataDefault, func }) => {
  // ⭐ ลบ geoData
  const [loading, setLoading] = useState(false);
  const [dataLoadDB2, setDataLoadDB2] = useState([]);
  const [dataLoadDB2Raw, setDataLoadDB2Raw] = useState(null);
  const [dataLawsuit, setDataLawsuit] = useState(null);
  const [geoRawData, setGeoRawData] = useState(null);
  const [activeKey, setActiveKey] = useState([]);
  const [isModalAddAddress, setIsModalAddAddress] = useState(false);
  const [personCurent, setPersonCurent] = useState();
  const [editingAddress, setEditingAddress] = useState();

  // ✅ โหลด GEO Data (ครั้งเดียว)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        });

        if (isMounted) {
          setGeoRawData(res.data);
          console.log("✅ geoRawData loaded", res.data);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("❌ Error loading geo data:", error);
          message.warning("ไม่สามารถโหลดข้อมูลจังหวัด อำเภอ ตำบลได้");
        }
      }
    };

    loadGeoData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ✅ โหลดข้อมูล Lawsuit เท่านั้น
  const loadLawsuitData = useCallback(async () => {
    if (!dataDefault) return null;

    setLoading(true);
    const controller = new AbortController();

    try {
      console.log("📋 Loading lawsuit data for:", dataDefault);

      const lawsuitRes = await axios.post(
        POST_LAWSUIT_DETAIL,
        {
          CONTNO: dataDefault.CONTNO,
          loanId: dataDefault.loanId,
          lawsuitId: dataDefault.lawsuitId || 0,
        },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        }
      );

      if (lawsuitRes?.data?.lawsuit) {
        console.log("✅ Lawsuit data loaded:", lawsuitRes.data.lawsuit);
        setDataLawsuit(lawsuitRes.data.lawsuit);
        return lawsuitRes.data.lawsuit;
      } else {
        throw new Error("ไม่พบข้อมูลคดี");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading lawsuit:", error);
        const errorMsg = error.response?.data?.message || error.message;
        message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [dataDefault]);

  // ✅ โหลดข้อมูลทั้งหมด (DB2 + Lawsuit)
  const loadAllData = useCallback(async () => {
    if (!open || !dataDefault) return;

    setLoading(true);
    let isMounted = true;
    const controller = new AbortController();

    try {
      console.log("📋 Loading all data for:", dataDefault);

      // โหลดทั้ง DB2 และ Lawsuit พร้อมกัน
      const [loanDB2Res, lawsuitRes] = await Promise.all([
        axios.post(
          POST_LOAN_DB2_V2,
          {
            [dataDefault.SCHEMA]: `'${dataDefault.CONTNO}'`,
          },
          {
            headers: HEADERS_EXPORT_BEN,
            signal: controller.signal,
          }
        ),
        axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: dataDefault.CONTNO,
            loanId: dataDefault.loanId,
            lawsuitId: dataDefault.lawsuitId || 0,
          },
          {
            headers: HEADERS_EXPORT_BEN,
            signal: controller.signal,
          }
        ),
      ]);

      if (!isMounted) return;

      // เก็บข้อมูล Lawsuit
      if (lawsuitRes?.data?.lawsuit) {
        console.log("✅ Lawsuit data loaded:", lawsuitRes.data.lawsuit);
        setDataLawsuit(lawsuitRes.data.lawsuit);
      }

      // เก็บข้อมูล DB2 และ Merge
      if (loanDB2Res?.data?.[0]) {
        console.log("✅ DB2 data loaded:", loanDB2Res.data[0]);
        setDataLoadDB2Raw(loanDB2Res.data[0]);

        const mergedData = mergeDataWithGuarantors(
          loanDB2Res.data[0],
          lawsuitRes?.data?.lawsuit,
          geoRawData
        );

        setDataLoadDB2(mergedData);
        console.log("✅ Merged data:", mergedData);
      } else {
        throw new Error("ไม่พบข้อมูล DB2");
      }
    } catch (error) {
      if (error.name !== "CanceledError" && isMounted) {
        console.error("❌ Error loading data:", error);
        const errorMsg = error.response?.data?.message || error.message;
        message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
        setDataLoadDB2([]);
        setDataLawsuit(null);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [open, dataDefault, geoRawData]);

  // ✅ Refresh เฉพาะ Lawsuit และ Merge ใหม่
  const refreshLawsuitAndMerge = useCallback(async () => {
    if (!dataLoadDB2Raw) {
      console.warn("⚠️ ไม่มีข้อมูล DB2 ดิบ, โหลดข้อมูลทั้งหมดใหม่");
      await loadAllData();
      return;
    }

    console.log("🔄 Refreshing lawsuit data only...");

    // โหลดเฉพาะ lawsuit
    const newLawsuitData = await loadLawsuitData();

    if (newLawsuitData) {
      // Merge ใหม่ด้วยข้อมูล DB2 เดิม + lawsuit ใหม่
      const mergedData = mergeDataWithGuarantors(
        dataLoadDB2Raw,
        newLawsuitData,
        geoRawData
      );

      setDataLoadDB2(mergedData);
      console.log("✅ Re-merged data with new lawsuit:", mergedData);
    }
  }, [dataLoadDB2Raw, geoRawData, loadLawsuitData, loadAllData]);

  // ✅ โหลดข้อมูลเมื่อเปิด Modal
  useEffect(() => {
    if (open) {
      loadAllData();
    }
  }, [open, loadAllData]);

  // ✅ Reset เมื่อเปิด Modal
  useEffect(() => {
    if (open) {
      setActiveKey([]);
    }
  }, [open]);

  const handleCancel = () => {
    func({
      action: "refresh",
      data: dataLawsuit,
    });
    close();
  };

  // ✅ Handler: เพิ่มที่อยู่ ทร.14
  const handleAddAddressTr = (person) => {
    console.log("เพิ่มที่อยู่ ทร.14 สำหรับ:", person);
    setPersonCurent(person);
    setEditingAddress(null);
    setIsModalAddAddress(true);
  };

  // ✅ Handler: Callback จาก AddAddressDetail
  const handleLoad = useCallback(
    async (data) => {
      console.log("📥 Callback from AddAddressDetail:", data);

      if (data?.action === "refresh") {
        await refreshLawsuitAndMerge();
        message.success("อัพเดทข้อมูลที่อยู่ ทร.14 สำเร็จ");
      }
    },
    [refreshLawsuitAndMerge]
  );

  // ✅ Merge Customer + Guarantors
  const mergeDataWithGuarantors = (dataLoadDB2, dataLawsuit, geoRawData) => {
    console.log("🔄 mergeDataWithGuarantors", { dataLoadDB2, dataLawsuit });

    let mergedData = [];

    // ✅ ฟังก์ชันแปลง code เป็นชื่อ
    const getGeoNameFromCode = (provCode, distCode, subDistCode) => {
      let provName = null;
      let distName = null;
      let subDistName = null;

      if (geoRawData) {
        const province = geoRawData.province?.find(
          (p) => String(p.provCode) === String(provCode)
        );
        provName = province?.provDesc || null;

        if (province && distCode) {
          const district = geoRawData.district?.find(
            (d) =>
              String(d.provCode) === String(provCode) &&
              String(d.distCode) === String(distCode)
          );
          distName = district?.distDesc || null;
        }

        if (province && distCode && subDistCode) {
          const subDistrict = geoRawData.subDistrict?.find(
            (s) =>
              String(s.provCode) === String(provCode) &&
              String(s.distCode) === String(distCode) &&
              String(s.subDistCode) === String(subDistCode)
          );
          subDistName = subDistrict?.subDistDesc || null;
        }
      }

      return { provName, distName, subDistName };
    };

    // ✅ ฟังก์ชันแปลง addressTr เป็น ADDRESS format
    const convertAddressTrToAddress = (addressTr) => {
      const geoNames = getGeoNameFromCode(
        addressTr.province,
        addressTr.district,
        addressTr.subdistrict
      );

      return {
        customerId: addressTr.customerId,
        ADDRNO: "4",
        ADDR1: addressTr.houseNumber || "",
        ADDR2: addressTr.road || "",
        MOOBAN: addressTr.village || "",
        SOI: addressTr.soi || "",
        TUMB: geoNames.subDistName || addressTr.subdistrict || "",
        AUMPDES: geoNames.distName || addressTr.district || "",
        PROVDES: geoNames.provName || addressTr.province || "",
        ZIP: addressTr.zipcode || "",
        TELP: "",
        houseRegistrationNumber: addressTr.houseRegistrationNumber || "",
        sourceType: "addressTr",
        addressTrRaw: addressTr,
        flagUse: addressTr.flagUse,
        CreatedAt: addressTr.CreatedAt,
      };
    };

    // 1. ✅ CUSTOMER
    if (dataLoadDB2?.CUSTOMER) {
      const customer = dataLoadDB2.CUSTOMER;
      const addressesDB2 = [];
      const addressesTr = [];

      if (customer.ADDRESS && customer.ADDRESS.length > 0) {
        customer.ADDRESS.forEach((addr, index) => {
          addressesDB2.push({
            ...addr,
            sourceType: "DB2",
            uniqueKey: `db2_customer_${customer.CUSCOD}_${index}`,
          });
        });
      }

      if (dataLawsuit?.loanOld?.addressTr) {
        dataLawsuit.loanOld.addressTr.forEach((addrTr, index) => {
          addressesTr.push({
            ...convertAddressTrToAddress(addrTr),
            uniqueKey: `tr_customer_${addrTr.customerId}_${index}`,
          });
        });
      }

      mergedData.push({
        garno: 0,
        CONTNO: customer.CONTNO || dataDefault?.CONTNO || "",
        CUSCOD: customer.CUSCOD,
        CUSTOMER_ID: dataLawsuit?.loanOld?.CUSTOMER_ID || customer.CUSTOMER_ID,
        SNAM: customer.SNAM,
        NAME1: customer.NAME1,
        NAME2: customer.NAME2,
        NAME: `${customer.SNAM || ""} ${customer.NAME1 || ""} ${
          customer.NAME2 || ""
        }`.trim(),
        BIRTHDT: customer.BIRTHDT,
        NATION: customer.NATION,
        OCCUP: customer.OCCUP,
        OFFIC: customer.OFFIC,
        addressesDB2: addressesDB2,
        addressesTr: addressesTr,
      });
    }

    // 2. ✅ GUARANTORS
    if (dataLoadDB2?.GUARANTORS && dataLoadDB2.GUARANTORS.length > 0) {
      dataLoadDB2.GUARANTORS.forEach((guarantor) => {
        const addressesDB2 = [];
        const addressesTr = [];
        let guarantorCustomerId = guarantor.CUSTOMER_ID || null;

        if (guarantor.ADDRESS && guarantor.ADDRESS.length > 0) {
          guarantor.ADDRESS.forEach((addr, index) => {
            addressesDB2.push({
              ...addr,
              sourceType: "DB2",
              uniqueKey: `db2_guarantor_${guarantor.CUSCOD}_${index}`,
            });
          });
        }

        if (dataLawsuit?.loanOld?.guarantors) {
          const matchedGuarantor = dataLawsuit.loanOld.guarantors.find(
            (g) => g.garno === guarantor.GARNO
          );

          if (matchedGuarantor) {
            guarantorCustomerId = matchedGuarantor.CUSTOMER_ID;

            if (
              matchedGuarantor.addressTr &&
              matchedGuarantor.addressTr.length > 0
            ) {
              matchedGuarantor.addressTr.forEach((addrTr, index) => {
                addressesTr.push({
                  ...convertAddressTrToAddress(addrTr),
                  uniqueKey: `tr_guarantor_${matchedGuarantor.CUSTOMER_ID}_${index}`,
                });
              });
            }
          }
        }

        mergedData.push({
          garno: guarantor.GARNO,
          CONTNO: guarantor.CONTNO || dataDefault?.CONTNO || "",
          GARNO: guarantor.GARNO,
          RELATN: guarantor.RELATN,
          CUSCOD: guarantor.CUSCOD,
          CUSTOMER_ID: guarantorCustomerId,
          SNAM: guarantor.SNAM,
          NAME1: guarantor.NAME1,
          NAME2: guarantor.NAME2,
          NAME: `${guarantor.SNAM || ""} ${guarantor.NAME1 || ""} ${
            guarantor.NAME2 || ""
          }`.trim(),
          BIRTHDT: guarantor.BIRTHDT,
          NATION: guarantor.NATION,
          OCCUP: guarantor.OCCUP,
          OFFIC: guarantor.OFFIC,
          addressesDB2: addressesDB2,
          addressesTr: addressesTr,
        });
      });
    }

    const sortedData = mergedData.sort((a, b) => {
      const contnoComparison = (a.CONTNO || "").localeCompare(
        b.CONTNO || "",
        undefined,
        { numeric: true }
      );
      if (contnoComparison !== 0) return contnoComparison;
      return (a.garno || 0) - (b.garno || 0);
    });

    return sortedData.map((item, index) => ({
      ...item,
      key: index + 1,
    }));
  };

  // ✅ หา GEO Info
  const getGeoInfo = (provDesc, distDesc, subDistDesc) => {
    if (!geoRawData)
      return { province: null, district: null, subDistrict: null };

    const province = geoRawData.province?.find((p) => p.provDesc === provDesc);
    const district = geoRawData.district?.find(
      (d) =>
        d.distDesc === distDesc &&
        String(d.provCode) === String(province?.provCode)
    );
    const subDistrict = geoRawData.subDistrict?.find(
      (s) =>
        s.subDistDesc === subDistDesc &&
        String(s.distCode) === String(district?.distCode) &&
        String(s.provCode) === String(province?.provCode)
    );

    return { province, district, subDistrict };
  };

  // ✅ แสดงประเภทที่อยู่
  const getAddressTypeName = (addrno) => {
    const types = {
      1: "ทะเบียนบ้าน",
      2: "ส่งเอกสาร",
      3: "ที่ทำงาน",
      4: "ทะเบียนราษฎร",
    };
    return types[String(addrno)] || "ไม่ระบุ";
  };

  // ✅ Columns สำหรับที่อยู่ยื่นกู้ (DB2)
  const addressDB2Columns = [
    {
      title: "ประเภท",
      dataIndex: "ADDRNO",
      key: "ADDRNO",
      width: 120,
      render: (addrno) => (
        <Tag color="purple">{getAddressTypeName(addrno)}</Tag>
      ),
    },
    {
      title: "ที่อยู่",
      key: "fullAddress",
      render: (_, record) => {
        const fullAddress = [
          record.ADDR1,
          record.ADDR2,
          record.MOOBAN,
          record.SOI,
        ]
          .filter(Boolean)
          .join(" ");
        return <Text>{fullAddress || "-"}</Text>;
      },
    },
    {
      title: "ตำบล",
      dataIndex: "TUMB",
      key: "TUMB",
      width: 120,
      render: (tumb, record) => {
        const geo = getGeoInfo(record.PROVDES, record.AUMPDES, tumb);
        return (
          <Space direction="vertical" size={0}>
            <Text>{tumb || "-"}</Text>
            {geo.subDistrict && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                ({geo.subDistrict.subDistCode})
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "อำเภอ",
      dataIndex: "AUMPDES",
      key: "AUMPDES",
      width: 120,
      render: (aumpdes, record) => {
        const geo = getGeoInfo(record.PROVDES, aumpdes, record.TUMB);
        return (
          <Space direction="vertical" size={0}>
            <Text>{aumpdes || "-"}</Text>
            {geo.district && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                ({geo.district.distCode})
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "จังหวัด",
      dataIndex: "PROVDES",
      key: "PROVDES",
      width: 120,
      render: (provdes) => {
        return (
          <Space direction="vertical" size={0}>
            <Text>{provdes || "-"}</Text>
          </Space>
        );
      },
    },
    {
      title: "รหัสไปรษณีย์",
      dataIndex: "ZIP",
      key: "ZIP",
      width: 100,
      align: "center",
    },
  ];

  // ✅ Columns สำหรับที่อยู่ ทร.14
  const addressTrColumns = [
    {
      title: "ประเภท",
      dataIndex: "ADDRNO",
      key: "ADDRNO",
      width: 120,
      render: (addrno) => (
        <Tag color="orange">{getAddressTypeName(addrno)}</Tag>
      ),
    },
    {
      title: "เลขทะเบียนบ้าน",
      dataIndex: "houseRegistrationNumber",
      key: "houseRegistrationNumber",
      width: 150,
      render: (text) => <Text>{text || "-"}</Text>,
    },
    {
      title: "ที่อยู่",
      key: "fullAddress",
      render: (_, record) => {
        const fullAddress = [
          record.ADDR1,
          record.ADDR2,
          record.MOOBAN,
          record.SOI,
        ]
          .filter(Boolean)
          .join(" ");
        return <Text>{fullAddress || "-"}</Text>;
      },
    },
    {
      title: "ตำบล",
      dataIndex: "TUMB",
      key: "TUMB",
      width: 120,
      render: (tumb) => <Text>{tumb || "-"}</Text>,
    },
    {
      title: "อำเภอ",
      dataIndex: "AUMPDES",
      key: "AUMPDES",
      width: 120,
      render: (aumpdes) => <Text>{aumpdes || "-"}</Text>,
    },
    {
      title: "จังหวัด",
      dataIndex: "PROVDES",
      key: "PROVDES",
      width: 120,
      render: (provdes) => <Text>{provdes || "-"}</Text>,
    },
    {
      title: "รหัสไปรษณีย์",
      dataIndex: "ZIP",
      key: "ZIP",
      width: 100,
      align: "center",
    },
    {
      title: "สร้างเมื่อ",
      dataIndex: "CreatedAt",
      key: "CreatedAt",
      width: 100,
      align: "center",
      render: (_, record) => {
        return (
          <Tag
            color={
              dayjs(record.CreatedAt).isAfter(dayjs().subtract(30, "day"))
                ? "green"
                : "red"
            }
          >
            {dayjs(record.CreatedAt).format("D MMM YY")}
          </Tag>
        );
      },
    },
  ];

  // ✅ สร้าง Collapse items
  const items = useMemo(() => {
    return dataLoadDB2.map((person, idx) => {
      const isCustomer = person.garno === 0;

      return {
        key: `person_${idx}`,
        label: (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical" size={5}>
              <Space>
                <Tag
                  color={isCustomer ? "blue" : "green"}
                  style={{ fontSize: 12 }}
                >
                  <strong>
                    {isCustomer ? "ผู้เช่าซื้อ" : `ผู้ค้ำ ${person.GARNO}`}
                  </strong>
                </Tag>
                {!isCustomer && (
                  <Tag color="orange" style={{ fontSize: 12 }}>
                    {person.RELATN}
                  </Tag>
                )}
              </Space>
              <div style={{ fontSize: 13, marginTop: 4 }}>{person.NAME}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                เลขบัตร: {person.CUSCOD}
              </div>
              {person.OFFIC && (
                <div style={{ fontSize: 12, color: "#666" }}>
                  อาชีพ: {person.OFFIC}
                </div>
              )}
            </Space>

            <Space direction="vertical" size={0} align="end">
              <Tag color="blue" style={{ fontSize: 11 }}>
                <HomeOutlined /> ยื่นกู้: {person.addressesDB2.length}
              </Tag>
              <Tag color="orange" style={{ fontSize: 11 }}>
                <HomeOutlined /> ทร.14: {person.addressesTr.length}
              </Tag>
            </Space>
          </Space>
        ),
        children: (
          <>
            {/* ที่อยู่ยื่นกู้ */}
            <Card
              title="ที่อยู่ยื่นกู้ (จากระบบ)"
              size="small"
              style={{ marginBottom: 10 }}
              styles={{
                header: {
                  background: "#e6f7ff",
                  borderBottom: "2px solid #91d5ff",
                },
              }}
            >
              {person.addressesDB2.length > 0 ? (
                <Table
                  dataSource={person.addressesDB2}
                  columns={addressDB2Columns}
                  rowKey="uniqueKey"
                  pagination={false}
                  size="small"
                  bordered
                />
              ) : (
                <Empty
                  description="ไม่มีข้อมูลที่อยู่ยื่นกู้"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>

            {/* ที่อยู่ ทร.14 */}
            <Card
              title={
                <Space
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <span>ที่อยู่ ทร.14 (ทะเบียนราษฎร)</span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={() => handleAddAddressTr(person)}
                  >
                    เพิ่มที่อยู่ ทร.14
                  </Button>
                </Space>
              }
              size="small"
              styles={{
                header: {
                  background: "#fff7e6",
                  borderBottom: "2px solid #ffd591",
                },
              }}
            >
              {person.addressesTr.length > 0 ? (
                <Table
                  dataSource={person.addressesTr}
                  columns={addressTrColumns}
                  rowKey="uniqueKey"
                  pagination={false}
                  size="small"
                  bordered
                />
              ) : (
                <Empty
                  description="ยังไม่มีข้อมูลที่อยู่ ทร.14"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>
          </>
        ),
      };
    });
  }, [dataLoadDB2, geoRawData]);

  return (
    <Modal
      title={
        <span style={{ fontSize: 17 }}>
          ข้อมูลที่อยู่จากระบบ - {dataDefault?.CONTNO}
        </span>
      }
      open={open}
      onCancel={handleCancel}
      width={1300}
      footer={null}
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Spin spinning={loading}>
          {dataLoadDB2.length > 0 ? (
            <Collapse
              items={items}
              accordion
              activeKey={activeKey}
              onChange={setActiveKey}
            />
          ) : (
            <Empty description="ไม่มีข้อมูล" />
          )}
        </Spin>
      </div>

      {isModalAddAddress && (
        <AddAddressDetail
          open={isModalAddAddress}
          close={() => setIsModalAddAddress(false)}
          currentPerson={personCurent}
          geoRawData={geoRawData}
          func={handleLoad}
          editingAddress={editingAddress}
        />
      )}
    </Modal>
  );
};

export default ImportAddressTr;
